/**
 * Why this clinician (2.6.2), two stages. Hard filters first — discipline / role,
 * required traits, patient hard constraints, shift and block feasibility, drive
 * within the shift's maximum from its center, compliance window, auth where the
 * payer gate applies — then scores across the feasible set (drive time,
 * continuity) with the weights shown as configuration. No territory filter in
 * either stage: drive from the shift center is the gravity; territory is a lens.
 */
import type { DemoState, Discipline, Episode, PlanVisit, RosterEntry } from "@caredna-marlin/utils/homeHealthDemo/state";
import { selectAuthCoverage } from "./authLedger";
import { estimateDriveMinutes } from "./calloutRanking";
import { clinicianDayFacts, providerIdFor } from "./clinicianDay";
import { evaluateConstraints } from "./constraints";
import { complianceWindowFor } from "./lupa";
import { hasTraits, isInRamp, isSocCapable, rosterEntries, visitTypeLabel } from "./roster";
import { territoryForZip } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";

/** A real engine option (from the product's scheduleOption) for one clinician. */
export interface EngineSlot {
  providerId: number;
  originDriveMinutes: number;
  totalScore: number;
  driveScore?: number;
  proximityScore?: number;
}

export type FilterKey = "discipline" | "traits" | "patientConstraints" | "shiftFeasibility" | "maxDrive" | "complianceWindow" | "auth";

export interface FilterResult {
  key: FilterKey;
  label: string;
  pass: boolean;
  detail: string;
}

export const FILTER_LABELS: Record<FilterKey, string> = {
  discipline: "Discipline / role",
  traits: "Required traits",
  patientConstraints: "Patient hard constraints",
  shiftFeasibility: "Shift and block feasibility",
  maxDrive: "Drive within the shift maximum",
  complianceWindow: "Compliance window",
  auth: "Auth (payer gate)",
};

export interface ClinicianExplanation {
  handle: string;
  displayName: string;
  discipline: Discipline;
  employment: RosterEntry["employment"];
  traits: string[];
  providerId?: number;
  feasible: boolean;
  filters: FilterResult[];
  driveMinutes?: number;
  driveIsEstimate: boolean;
  continuity: { visitsWithPatient: number; onEpisodeTeam: boolean };
  scores?: { drive: number; continuity: number; total: number; engineTotal?: number };
  chosen: boolean;
}

export interface WhyThisClinician {
  visit: PlanVisit;
  episode: Episode;
  patientRef: string;
  patientName: string;
  visitLabel: string;
  weights: { driveTime: number; continuity: number };
  filterOrder: FilterKey[];
  candidates: ClinicianExplanation[];
  chosenHandle?: string;
  gated: boolean;
}

/**
 * The filters a reader sees, in order. `maxDrive` is deliberately absent: the
 * 65-minute guardrail still decides eligibility (it stays in `filters`), it is
 * simply never shown or named on screen.
 */
const FILTER_ORDER: FilterKey[] = ["discipline", "traits", "patientConstraints", "shiftFeasibility", "complianceWindow", "auth"];

export function explainPlacement(state: DemoState, planVisitId: string, inputs: { engineSlots?: EngineSlot[] } = {}): WhyThisClinician | undefined {
  const visit = state.planVisits[planVisitId];
  if (!visit) return undefined;
  const episode = state.episodes[visit.episodeId];
  const patient = episode ? state.patients[episode.patientRef] : undefined;
  if (!episode || !patient) return undefined;
  const config = state.config;
  const vt = config.visitTypes[visit.visitTypeKind];
  const requiredTraits = Array.from(new Set([...vt.requiredTraits, ...visit.requiredTraits]));
  const gated = patient.payer === "MA";
  const coverage = selectAuthCoverage(episode, Object.values(state.planVisits).filter((v) => v.episodeId === episode.id && v.status !== "canceled"), gated);
  const authState = coverage.byVisitId[visit.id];
  const window = complianceWindowFor(visit, episode);
  const patientTerritory = territoryForZip(config.branch, patient.zip);
  const engineByProvider = new Map((inputs.engineSlots ?? []).map((s) => [s.providerId, s]));
  const teamHandles = new Set(Object.values(state.planVisits).filter((v) => v.episodeId === episode.id && v.providerHandle && v.id !== visit.id).map((v) => v.providerHandle!));
  const patientId = state.meta.bindings.patientIdsByRef[patient.ref];

  const candidates: ClinicianExplanation[] = rosterEntries(state).map((entry) => {
    const providerId = providerIdFor(state, entry);
    const filters: FilterResult[] = [];
    const disciplineOk = vt.disciplines.includes(entry.discipline);
    const roleOk = vt.isAssessment ? isSocCapable(entry, visit.targetDate) : true;
    filters.push({
      key: "discipline",
      label: FILTER_LABELS.discipline,
      pass: disciplineOk && roleOk,
      detail: !disciplineOk ? `${entry.discipline} does not deliver ${vt.label}` : !roleOk ? (isInRamp(entry, visit.targetDate) ? `No solo assessments in ramp (week ${entry.rampWeek} of ${entry.rampWeeks})` : "Assessments need OASIS-competent") : `${entry.discipline} delivers ${vt.label}`,
    });
    const traitOk = hasTraits(entry, requiredTraits);
    filters.push({ key: "traits", label: FILTER_LABELS.traits, pass: traitOk, detail: requiredTraits.length ? (traitOk ? `Carries ${requiredTraits.join(", ")}` : `Missing ${requiredTraits.filter((t) => !entry.traits.includes(t)).join(", ")}`) : "No special traits required" });
    const hard = evaluateConstraints(patient, visit, entry);
    filters.push({ key: "patientConstraints", label: FILTER_LABELS.patientConstraints, pass: hard.length === 0, detail: hard.length ? hard.map((h) => h.reason).join("; ") : patient.constraints.length ? "Meets the patient's hard constraints" : "No hard constraints on file" });
    const facts = clinicianDayFacts(state, entry, visit.targetDate);
    const slack = facts.shiftMinutes - facts.blockMinutes - facts.bookedMinutes - facts.driveMinutes + (visit.providerHandle === entry.handle && visit.status !== "placeholder" ? vt.plannedMinutes : 0);
    const shiftOk = facts.hasShift && slack >= vt.plannedMinutes;
    filters.push({ key: "shiftFeasibility", label: FILTER_LABELS.shiftFeasibility, pass: shiftOk, detail: !facts.hasShift ? "Not on shift that day" : shiftOk ? `${Math.round(slack / 60 * 10) / 10}h open around blocks for a ${vt.plannedMinutes} min visit` : `Only ${Math.max(0, Math.round(slack / 60 * 10) / 10)}h open for a ${vt.plannedMinutes} min visit` });
    const engine = providerId !== undefined ? engineByProvider.get(providerId) : undefined;
    const driveMinutes = engine ? engine.originDriveMinutes : facts.hasShift ? estimateDriveMinutes(facts.supplyTerritory, patientTerritory) : undefined;
    const driveOk = driveMinutes !== undefined && driveMinutes <= config.maxDriveMinutes;
    filters.push({ key: "maxDrive", label: FILTER_LABELS.maxDrive, pass: driveOk, detail: driveMinutes === undefined ? "No shift center to measure from" : `${driveMinutes} min${engine ? "" : " (estimate)"} from the shift center · maximum ${config.maxDriveMinutes} min` });
    const inWindow = visit.targetDate >= window.start && visit.targetDate <= window.end;
    filters.push({ key: "complianceWindow", label: FILTER_LABELS.complianceWindow, pass: inWindow, detail: `${window.start} – ${window.end}${visit.rule === "recert" ? " (days 56–60)" : " (SOC-anchored week)"}` });
    const authOk = !gated || authState !== "beyondAuth";
    filters.push({ key: "auth", label: FILTER_LABELS.auth, pass: authOk, detail: !gated ? "Medicare FFS — not gated" : authOk ? "Inside authorized units" : "Beyond authorized units — placeholder until the auth event lands" });

    const feasible = filters.every((f) => f.pass);
    const visitsWithPatient = patientId !== undefined && providerId !== undefined ? new Set(state.server.visitResources.filter((vr) => vr.providerId === providerId && vr.patientId === patientId && vr.eventStatus === "complete").map((vr) => vr.visitId)).size : 0;
    const onEpisodeTeam = teamHandles.has(entry.handle);
    const continuity = { visitsWithPatient, onEpisodeTeam };
    let scores: ClinicianExplanation["scores"];
    if (feasible && driveMinutes !== undefined) {
      const drive = Math.max(0, Math.round(100 * (1 - driveMinutes / config.maxDriveMinutes)));
      const cont = onEpisodeTeam || visitsWithPatient > 0 ? 100 : 0;
      const w = config.scoreWeightsDisplay;
      const total = Math.round((w.driveTime * drive + w.continuity * cont) / Math.max(1, w.driveTime + w.continuity));
      scores = { drive, continuity: cont, total, engineTotal: engine?.totalScore };
    }
    return { handle: entry.handle, displayName: entry.displayName, discipline: entry.discipline, employment: entry.employment, traits: entry.traits, providerId, feasible, filters, driveMinutes, driveIsEstimate: !engine, continuity, scores, chosen: visit.providerHandle === entry.handle };
  });

  candidates.sort((a, b) => Number(b.chosen) - Number(a.chosen) || Number(b.feasible) - Number(a.feasible) || (b.scores?.total ?? -1) - (a.scores?.total ?? -1) || a.displayName.localeCompare(b.displayName));
  return {
    visit,
    episode,
    patientRef: patient.ref,
    patientName: patient.displayName,
    visitLabel: visitTypeLabel(config, visit.visitTypeKind),
    weights: config.scoreWeightsDisplay,
    filterOrder: FILTER_ORDER,
    candidates,
    chosenHandle: visit.providerHandle,
    gated,
  };
}

/** Front-load geometry (2.6.1): visits per SOC-anchored week per discipline. */
export function frontLoadCounts(visits: PlanVisit[], weeks: number): { discipline: Discipline; counts: number[] }[] {
  const byDiscipline = new Map<Discipline, number[]>();
  for (const v of visits) {
    if (v.status === "canceled") continue;
    const arr = byDiscipline.get(v.discipline) ?? Array.from({ length: weeks }, () => 0);
    if (v.weekIndex >= 0 && v.weekIndex < weeks) arr[v.weekIndex] += 1;
    byDiscipline.set(v.discipline, arr);
  }
  return [...byDiscipline.entries()].map(([discipline, counts]) => ({ discipline, counts }));
}
